import React, { useRef, useState, useCallback } from "react";
import AsyncSelect from "react-select/async";
import _ from "lodash";

const AutocompleteDropdown = ({
  loadOptions,
  onChange,
  value,
  customStyles,
  ...props
}) => {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState(""); // Track input value
  const cacheRef = useRef({}); // Persistent cache across renders

  const colorStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: "#f1f1f2",
      border: "none",
      color: "#002a4e",
      fontSize: "13px",
      fontWeight: "600",
      cursor: "pointer",
    }),
    placeholder: (defaultStyles) => ({
      ...defaultStyles,
      color: "#002a4e",
      fontSize: "13px",
      fontWeight: "600",
    }),
  };

  const customStyless = {
    menuList: (provided) => ({
      ...provided,
      maxHeight: 120,
    }),
  };

  const mergedStyles = customStyles
    ? { ...colorStyles, ...customStyles }
    : colorStyles;

  // Debounced fetchOptions logic
  const debouncedFetchOptions = useCallback(
    _.debounce(async (input, resolve) => {
      if (cacheRef.current[input]) {
        resolve(cacheRef.current[input]);
        return;
      }

      try {
        const response = await loadOptions(input);
        cacheRef.current[input] = response; // Cache the result
        resolve(response);
      } catch (error) {
        console.error("Error fetching options:", error);
        resolve([]); // Return empty array on error
      }
    }, 300),
    [loadOptions]
  );

  // Wrapper function to ensure a Promise is returned
  const fetchOptions = useCallback(
    (input) =>
      new Promise((resolve) => {
        debouncedFetchOptions(input, resolve);
      }),
    [debouncedFetchOptions]
  );

  const handleInputChange = (newInputValue) => {
    setInputValue(newInputValue);
    fetchOptions(newInputValue)
      .then((result) => setOptions(result))
      .catch((error) => console.error("Error loading options:", error));
  };

  return (
    <AsyncSelect
      {...props}
      loadOptions={fetchOptions} // This works with cached values
      cacheOptions={false}
      defaultOptions
      onChange={onChange}
      value={value}
      styles={mergedStyles}
      classNamePrefix="country_dropdown"
      isDisabled={props.disabled}
      onInputChange={handleInputChange} // Debounced handler
      inputValue={inputValue} // Sync input state with the dropdown
      options={options}
    />
  );
};

export default AutocompleteDropdown;
